import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Crown, Users, Copy, Check, LogOut, ArrowLeft, MailCheck } from "lucide-react";
import axios from "axios";
import { signInWithGoogle } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
    const location = useLocation();
    const navigate = useNavigate();
    const [user, setUser] = useState(location.state?.user || null);
    const [authed, setAuthed] = useState(location.state?.user ? true : null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (location.state?.user) return;
        const checkAuth = async () => {
            try {
                const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
                setUser(data);
                setAuthed(true);
            } catch {
                setAuthed(false);
            }
        };
        checkAuth();
    }, [location.state]);

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch {}
        setUser(null);
        setAuthed(false);
        navigate("/");
    };

    if (authed === null) {
        return (
            <main data-testid="account-loading" className="flex min-h-screen items-center justify-center bg-cream">
                <Loader2 size={32} className="animate-spin text-lavender" />
            </main>
        );
    }

    if (authed === false) {
        return (
            <main data-testid="account-signin" className="flex min-h-screen items-center justify-center bg-cream px-6 text-navy">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                    className="w-full max-w-md rounded-3xl border-2 border-warmgray bg-ivory p-10 text-center shadow-xl shadow-navy/5"
                >
                    <Link to="/" data-testid="account-logo">
                        <img src="/logo.png" alt="Ago GenApp" className="mx-auto h-12 w-auto" />
                    </Link>
                    <h1 className="mt-6 font-display text-2xl md:text-3xl">Your family account.</h1>
                    <p className="mt-3 text-sm leading-relaxed text-navy">
                        Sign in to see your supporter tier, your invite link, and the family in your circle.
                    </p>
                    <button
                        data-testid="google-signin-button"
                        onClick={signInWithGoogle}
                        className="btn-gradient mt-7 w-full rounded-full py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                    >
                        Sign in with Google
                    </button>
                    <Link to="/" data-testid="account-back-home" className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-navy/70">
                        <ArrowLeft size={15} /> Back Home
                    </Link>
                </motion.div>
            </main>
        );
    }

    const supporter = user.supporter;
    const inviteUrl = supporter ? `${window.location.origin}/invite/${supporter.invite_code}` : "";
    const copyInvite = async () => {
        try {
            await navigator.clipboard.writeText(inviteUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };

    return (
        <main data-testid="account-page" className="min-h-screen bg-cream text-navy">
            <header className="border-b border-warmgray bg-cream/80 backdrop-blur-xl">
                <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
                    <Link to="/" data-testid="account-logo" aria-label="Ago GenApp home">
                        <img src="/logo.png" alt="Ago GenApp" className="h-10 w-auto" />
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            data-testid="account-back-home"
                            className="flex items-center gap-2 rounded-full border-2 border-navy px-5 py-2 text-sm font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            <ArrowLeft size={15} /> Back Home
                        </Link>
                        <button
                            data-testid="logout-button"
                            onClick={logout}
                            aria-label="Sign out"
                            className="text-navy/70 transition-colors duration-300 hover:text-navy"
                        >
                            <LogOut size={19} />
                        </button>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-4xl px-6 py-14">
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}>
                    <div className="flex items-center gap-5">
                        {user.picture ? (
                            <img src={user.picture} alt={user.name} className="h-16 w-16 rounded-full border-2 border-warmgray object-cover" referrerPolicy="no-referrer" />
                        ) : (
                            <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-warmgray bg-ivory font-display text-2xl">
                                {(user.name || user.email || "?")[0].toUpperCase()}
                            </div>
                        )}
                        <div>
                            <h1 data-testid="account-name" className="font-display text-3xl tracking-tight">{user.name || "Welcome"}</h1>
                            <p data-testid="account-email" className="mt-1 text-sm text-navy">{user.email}</p>
                        </div>
                    </div>

                    {supporter ? (
                        <div className="mt-10 space-y-6">
                            <div data-testid="account-tier-card" className="rounded-3xl border-2 border-warmgray bg-ivory p-7">
                                <div className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                                    <Crown size={14} /> Founding Supporter
                                </div>
                                <p className="mt-3 font-display text-3xl">{supporter.tier_name}</p>
                                <p className="mt-2 text-sm text-navy">
                                    ${supporter.amount_paid?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    {supporter.is_recurring ? " · monthly" : " · one-time"} · thank you for believing early
                                </p>
                            </div>

                            <div data-testid="account-invite-card" className="rounded-3xl border-2 border-warmgray bg-ivory p-7">
                                <div className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                                    <Users size={14} /> Your invite link · {supporter.invite_uses || 0} joined
                                </div>
                                <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                                    <p className="flex-1 break-all rounded-xl bg-[#FDFBF9] px-4 py-3 font-mono text-xs text-navy">{inviteUrl}</p>
                                    <button
                                        data-testid="account-copy-invite"
                                        onClick={copyInvite}
                                        className="btn-gradient flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                                    >
                                        {copied ? <Check size={15} /> : <Copy size={15} />}
                                        {copied ? "Copied!" : "Copy"}
                                    </button>
                                </div>
                                <Link
                                    data-testid="account-circle-link"
                                    to={`/circle/${supporter.session_id}`}
                                    className="mt-4 inline-block rounded-full border-2 border-navy px-6 py-2.5 text-xs font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5"
                                >
                                    View Your Family Circle
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <div data-testid="account-not-supporter" className="mt-10 rounded-3xl border-2 border-warmgray bg-ivory p-8">
                            {user.waitlist_status ? (
                                <div className="flex items-start gap-4">
                                    <MailCheck size={26} className="mt-1 shrink-0 text-lavender" />
                                    <div>
                                        <h2 className="font-display text-2xl">You’re on the waitlist.</h2>
                                        <p className="mt-2 text-sm leading-relaxed text-navy">
                                            Your beta invitation is on its way in the order you joined. Want in sooner and forever? Founding Supporters go first.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <h2 className="font-display text-2xl">You haven’t joined yet.</h2>
                                    <p className="mt-2 text-sm leading-relaxed text-navy">
                                        Become a Founding Supporter or join the waitlist to be first inside the private beta.
                                    </p>
                                </div>
                            )}
                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <Link to="/#support" data-testid="account-support-cta" className="btn-gradient rounded-full px-8 py-3.5 text-center text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">
                                    Become a Founding Supporter
                                </Link>
                                <Link to="/#waitlist" data-testid="account-waitlist-cta" className="rounded-full border-2 border-navy px-8 py-3.5 text-center text-sm font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5">
                                    Join the Waitlist
                                </Link>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </main>
    );
}
